import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { useLocationsContext } from "@/pages/configurations/locations/context/Location";
import { useRootTypeContext } from "@/pages/configurations/rootTypes/context/RootTypeContext";
import {
	Button,
	CircularProgress,
	FormControl,
	FormGroup,
	FormLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	Switch,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import {
	Controller,
	ControllerRenderProps,
	SubmitHandler,
	useForm,
} from "react-hook-form";
import { useRootStateContext } from "../../context/RootStateContext";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Root } from "../../models/Root";
import { createRoot } from "../../repositories/Root";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		checkbox: {
			userSelect: "none",
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const CreateRoot = ({ show, onHide }: Props) => {
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("");
	const [active, setActive] = useState(true);

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const classes = useStyles();

	const {
		state: { rootTypes },
	} = useRootTypeContext();
	const {
		state: { rootStates },
	} = useRootStateContext();
	const {
		state: { locations },
	} = useLocationsContext();

	const {
		register,
		control,
		setValue,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<Root>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};
	const onSubmit: SubmitHandler<Root> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await createRoot(data);

			if (res.ok) {
				setMessage("The root was created successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage(`Error, please try again`);
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	const handlerChangeIsActive = (
		field: ControllerRenderProps<Root, "needsAttention">,
	) => {
		field.onChange(!field.value);
		setActive((value) => !value);
	};

	useEffect(() => {
		if (show) {
			setActive(true);

			reset();
		}
	}, [show]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="createRoot"
					title="Create a new root"
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "200px" }}
								>
									{/* <LazyLoading /> */}
									<CircularProgress size="5rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid container spacing={4}>
										<Grid item md={3} xs={12}>
											<Controller
												name="rootId"
												rules={{
													required: true,
													validate: { positive: (v) => v != 0 },
												}}
												control={control}
												render={({ field }) => (
													<TextField
														label="Root ID"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.rootId}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.rootId?.type === "positive" && (
												<span className={classes.errorLabel}>
													Root ID must be different from 0
												</span>
											)}
											{errors.rootId?.type === "required" && (
												<span className={classes.errorLabel}>
													Root ID is required
												</span>
											)}
										</Grid>
										<Grid item md={9} xs={12}>
											<Controller
												name="rootName"
												rules={{ required: true }}
												control={control}
												render={({ field }) => (
													<TextField
														label="Name"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.rootName}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.rootName?.type === "required" && (
												<span className={classes.errorLabel}>
													Name is required
												</span>
											)}
										</Grid>

										<Grid item md={6} xs={12}>
											<Controller
												name="rootTypeId"
												control={control}
												rules={{
													required: true,
													validate: { positive: (v) => v != 0 },
												}}
												render={({ field }) => (
													<FormControl fullWidth>
														<InputLabel
															variant="standard"
															htmlFor="uncontrolled-native"
														>
															Root type
														</InputLabel>
														<Select
															error={!!errors.rootTypeId}
															value={field.value}
															onChange={field.onChange}
														>
															{rootTypes.map((type) => {
																return (
																	<MenuItem
																		value={type.rootTypeId}
																		key={type.rootTypeId}
																	>
																		{type.rootTypeName}
																	</MenuItem>
																);
															})}
														</Select>
													</FormControl>
												)}
											/>
											{errors.rootTypeId?.type === "positive" && (
												<span className={classes.errorLabel}>
													This field is required
												</span>
											)}
										</Grid>
										<Grid item md={3}>
											<Typography>Status</Typography>
											<Controller
												name="isActive"
												control={control}
												render={({ field }) => (
													<FormGroup>
														<Switch
															// className='switchBase'
															color="primary"
															defaultChecked={true}
															value={field.value}
															onChange={field.onChange}
														/>

														<FormLabel component="legend">
															{field.value ? "Active" : "Inactive"}
														</FormLabel>
													</FormGroup>
												)}
											/>
										</Grid>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions style={{ padding: "2rem" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>

				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					{messageType == "success" ? (
						<Alert severity="success" onClose={handleClose}>
							{message}
						</Alert>
					) : (
						<Alert severity="error" onClose={handleClose}>
							{message}
						</Alert>
					)}
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
